import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BwjPageContent } from '../../../../framework/components/bwj-page-content';
import { BwjPageHeader } from '../../../../framework/components/bwj-page-header';
import { AppRoutes, useProjectId } from '../../../app-routes';
import * as formService from '../../form-services/assess-record-main';
import * as doorService from '../../form-services/assess-record-door';
import * as laborService from '../../form-services/assess-record-labor';
import * as rejectService from '../../form-services/cancellation-editor';
import { BwjViewManager, BwjViewState } from '../../../../framework/components/bwj-view-manager';
import { useStandardErrorState } from '../../../../entities/view-manager/error';
import { IProjectAssessmentEditingModel, ProjectAssessmentEditingModel } from '../../../../entities-generated/models/project/project-assessment-editing-model';
import { ProjectAssessmentApiClient } from '../../../../services/project-assessment';
import { BwjGrid, Row, Col } from '../../../../framework/components/bwj-layout';
import { Spacing } from '../../../../framework/components/bwj-layout/styles';
import { BwjTextField } from '../../../../framework/components/bwj-text-field';
import { BwjValidityState } from '../../../../framework/entities/bwj-validity-state';
import { BwjDateField } from '../../../../framework/components/bwj-date-field';
import { BwjUploadField } from '../../../../framework/components/bwj-upload';
import { BwjImageBrowser, IBrowserImage } from '../../../../framework/components/bwj-image-browser';
import { isNotNullOrWhitespace } from '../../../../framework/utils';
import { ImageApiClient } from '../../../../services/image';
import { useToast } from '../../../../framework/components/bwj-toast/context';
import { MessageBarType } from '@fluentui/react';
import { BwjText, BwjTextSize } from '../../../../framework/components/bwj-text';
import { BwjLinkButton } from '../../../../framework/components/bwj-link-button';
import { ProposedDoorListing } from '../../components/proposed-door-listing';
import { DoorAssessment, IDoorAssessment } from '../../../../entities-generated/common/door-assessment';
import { getNowUnixTimestamp } from '../../../../framework/utils/date-time';
import { AssessmentDoorEditingModel } from '../../entities/assess-rec-door-fm';
import { EventNotificationService } from '../../../../framework/services/event-notification';
import { ProjectEvents } from '../../events';
import { ProposedDoorEditorPanel } from '../../components/proposed-door-edit-panel';
import { remove, replaceFirstWhere } from '../../../../framework/utils/collection';
import { BwjConfirmationDialog, IConfirmationDialogDisplayProps } from '../../../../framework/components/bwj-confirmation-dialog';
import { ILaborCallout } from '../../../../entities-generated/common/labor-callout';
import { LaborEditorPanel } from '../../components/labor-edit-panel';
import { IMaterialCallout } from '../../../../entities-generated/common/material-callout';
import { MaterialEditorPanel } from '../../components/material-edit-panel';
import { useNavigator } from '../../../../framework/services/navigation';
import { ProjectCancellationPanel } from '../../components/cancellation-panel';
import { IProjectCancellationPanelLaunchArgs } from '../../entities/cancellation-args';
import { BwjEventArgs } from '../../../../framework/entities/bwj-event';

const subscriberId = 'Page.RecordCompletedAssessment';

export const PageRecordCompletedAssessment: React.FunctionComponent = () => {
  const nav = useNavigator();
  const { showToast } = useToast();
  const projectId = useProjectId();
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const loadingMessage = useRef('Preparing assessment...');
  const errorState = useStandardErrorState();
  const formModel = formService.useFormModel(subscriberId);
  const taxFormImage = useMemo(() => {
    return isNotNullOrWhitespace(formModel.taxExemptionFormUrl)
      ? [{ src: formModel.taxExemptionFormUrl }] : [];
  }, [formModel]);
  const hasUnsavedChanges = formService.useHasUnsavedChanges(formModel, true);
  const editorModel = useRef<IProjectAssessmentEditingModel>(new ProjectAssessmentEditingModel());
  const [launchConfirmationDialogArgs, setLaunchConfirmationDialogArgs]
    = useState<BwjEventArgs<IConfirmationDialogDisplayProps | undefined> | undefined>(undefined);

  const showLoadingView = (message: string) => {
    loadingMessage.current = message;
    setViewState(BwjViewState.Loading);
  }

  useEffect(() => {
    showLoadingView('Loading assessment...');

    ProjectAssessmentApiClient.getAssessmentBooking(projectId)
    .then(resp => {
      editorModel.current = resp;
      formService.initializeService(resp);
      setViewState(BwjViewState.Ready);
    })
    .catch(e => {
      console.log(e);
      setViewState(BwjViewState.Error);
    });
  }, [projectId]);

  const onClearTaxForm = (image: IBrowserImage) => {
    ImageApiClient.removeImage(image.src)
    .then(() => {
      formService.taxExemptionFormUrl.onUpdate('');
    })
    .catch(e => {
      console.log(e);
      showToast(MessageBarType.error, 'Form removal failed');
    });
  };

  const onEditDoor = (door: IDoorAssessment) => {
    const em = editorModel.current;
    const args = new AssessmentDoorEditingModel(
      door, em.doorTypeOptions, em.doorSizeOptions,
      em.doorHandingOptions, em.standardLaborOptions,
      em.standardMaterialOptions);
    EventNotificationService.dispatch(ProjectEvents.AssessmentEditorPanel_Launch, args);
  }

  const onAddDoor = () => {
    const door = new DoorAssessment();
    door.id = getNowUnixTimestamp().toString();
    onEditDoor(door);
  }

  const onDoorUpdated = (door: IDoorAssessment) => {
    const doors = replaceFirstWhere(formModel.installationAssessments.value, d => d.id === door.id, door, true);
    formService.installationAssessments.onUpdate(doors);
  };

  const onRemoveDoor = (door: IDoorAssessment) => {
    const doors = remove(formModel.installationAssessments.value, d => d.id === door.id);
    formService.installationAssessments.onUpdate(doors);
  }
  const onDoorRemovalRequested = (door: IDoorAssessment) => {
    const args = {
      title: 'Remove Proposed Door',
      message: 'This cannot be undone, and all information will be lost - are you sure?',
      proceedPrompt: 'Yes, remove',
      cancelPrompt: 'Cancel',
      onProceedRequested: onRemoveDoor,
      context: door,
    };
    setLaunchConfirmationDialogArgs(new BwjEventArgs(args));
  }

  const onLaborRemovalRequested = (labor: ILaborCallout, onConfirmed: (l: ILaborCallout) => void) => {
    const confirmAction = () => {
      onConfirmed(labor);
      EventNotificationService.dispatch(ProjectEvents.AssessmentEditorPanelFreeToClose);
    }
    const cancelAction = () => EventNotificationService.dispatch(ProjectEvents.AssessmentEditorPanelFreeToClose);
    const args = {
      title: 'Remove Additional Work',
      message: 'This cannot be undone, and all information, including material callouts, will be lost - are you sure?',
      proceedPrompt: 'Yes, remove',
      cancelPrompt: 'Cancel',
      onProceedRequested: confirmAction,
      onCancelRequested: cancelAction,
      context: labor,
    };
    setLaunchConfirmationDialogArgs(new BwjEventArgs(args));
  };

  const onLaborUpdated = (labor: ILaborCallout) => {
    const workItems = doorService.laborScope.getOutputValue();
    const laborScope = replaceFirstWhere(workItems, w => w.id === labor.id, labor, true);
    doorService.laborScope.onUpdate(laborScope);
  };

  const onMaterialUpdated = (material: IMaterialCallout) => {
    const exMaterials = laborService.materials.getOutputValue();
    const materials = replaceFirstWhere(exMaterials, m => m.id === material.id, material, true);
    laborService.materials.onUpdate(materials);
  };

  const onRecordAssessment = () => {
    formService.validateAndUpdateModel()
    .then(result => {
      if(result.isValid === false) { return undefined; }

      showLoadingView('Saving assessment...');
      const request = formService.toRecordRequest(false);
      return ProjectAssessmentApiClient.recordProjectAssessment(request, projectId);
    })
    .then(resp => {
      if(resp === undefined) { return; }

      if(resp.success) {
        formService.setReversionState();
        formService.revertChanges();

        showToast(MessageBarType.success, 'Assessment successfully recorded');
        nav.goTo(AppRoutes.openProjects.url());
      } else {
        showToast(MessageBarType.error, resp.message);
        setViewState(BwjViewState.Ready);
      }
    })
    .catch(e => {
      console.log(e);
      showToast(MessageBarType.error, 'Error - save failed');
      setViewState(BwjViewState.Ready);
    });
  }

  const onConfirmRejectProject = () => {
    formService.validateAndUpdateModel()
    .then(result => {
      if(result.isValid === false) { return; }

      const args: IProjectCancellationPanelLaunchArgs = {
        id: projectId,
        title: 'Reject Project'
      };
      EventNotificationService.dispatch(ProjectEvents.ProjectCancellationPanel_Launch, args);
    });
  }

  const onRejectProject = () => {
    showLoadingView('Updating project status...');

    const request = formService.toRecordRequest(true);
    request.notes = rejectService.toValueModel().rationale;
    ProjectAssessmentApiClient.recordProjectAssessment(request, projectId)
    .then(() => {
      formService.setReversionState();
      formService.revertChanges();

      showToast(MessageBarType.info, 'Project rejected by management');
      nav.goTo(AppRoutes.openProjects.url());
    })
    .catch(e => {
      console.log(e);
      showToast(MessageBarType.error, 'Error - action failed');
      setViewState(BwjViewState.Ready);
    });
  }

  return (
    <>
    <ProjectCancellationPanel onProceedWithAction={onRejectProject} />
    <ProposedDoorEditorPanel
      onDoorUpdated={onDoorUpdated}
      onRemoveLabor={onLaborRemovalRequested}
    />
    <LaborEditorPanel
      onLaborUpdated={onLaborUpdated}
    />
    <MaterialEditorPanel
      onMaterialUpdated={onMaterialUpdated}
    />
    <BwjConfirmationDialog
      displayArgs={launchConfirmationDialogArgs}
    />
    <BwjViewManager
      state={viewState}
      loadingState={{
        message: loadingMessage.current
      }}
      errorState={errorState}
    >
      <BwjPageHeader
        title={'Document Project Assessment'}
        titleIconName={AppRoutes.recordCompletedAssessment.icon}
        actionButtons={[{
          id: 'btnReject',
          name: 'Reject project',
          iconName: 'BlockedSite',
          onClick: onConfirmRejectProject,
        }, {
          id: 'btnSave',
          name: 'Save assessment',
          iconName: 'Save',
          disabled: hasUnsavedChanges === false,
          onClick: onRecordAssessment,
        }]}
      />
      <BwjPageContent contentDescription={'primary content (no subcontent)'}>
        <BwjGrid>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={6}>
              <BwjDateField
                id={formService.datePerformed.id}
                required={true}
                label={formService.datePerformed.description}
                onUpdate={formService.datePerformed.onUpdate}
                input={formModel.datePerformed}
              />
            </Col>
            <Col small={12} large={6}>
              <BwjTextField
                id={formService.commuteTimeBlocks.id}
                required={true}
                label={formService.commuteTimeBlocks.description}
                onUpdate={formService.commuteTimeBlocks.onUpdate}
                input={formModel.commuteTimeBlocks}
                filter={/^\d*$/}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={8}>
              <BwjUploadField
                id={formService.taxExemptionFormUrlUploader.id}
                required={false}
                label={formService.taxExemptionFormUrlUploader.description}
                onUpdate={formService.taxExemptionFormUrlUploader.onUpdate}
                input={formModel.taxExemptionFormUrlUploader}
                accept={['jpg', 'jpeg', 'png']}
                disabled={formModel.taxExemptionFormUrlUploader.validity === BwjValidityState.Indeterminate}
                allowMultipleFiles={false}
              />
            </Col>
            <Col small={12} large={4}>
              <BwjImageBrowser
                id={formService.taxExemptionFormUrl.id}
                images={taxFormImage}
                displayIndex={0}
                loadImages={true}
                imageWidth={400}
                contextButton={{
                  icon: 'Delete',
                  onClick: onClearTaxForm,
                }}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12} large={6}>
              <BwjTextField
                id={formService.notes.id}
                required={false}
                label={formService.notes.description}
                onUpdate={formService.notes.onUpdate}
                input={formModel.notes}
                multiline={true}
              />
            </Col>
            <Col small={12} large={6}>
              <BwjTextField
                id={formService.customerNotes.id}
                required={false}
                label={formService.customerNotes.description}
                onUpdate={formService.customerNotes.onUpdate}
                input={formModel.customerNotes}
                multiline={true}
              />
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12}>
              <BwjTextField
                id={formService.discounts.id}
                required={false}
                label={formService.discounts.description}
                onUpdate={formService.discounts.onUpdate}
                input={formModel.discounts}
                multiline={true}
              />
              <BwjText
                textSize={BwjTextSize.xSmall}
              >
                Format: [description/code]%[percentage discount (optional)]|[flat discount (optional)]&gt;YYYY-M-D
              </BwjText>
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12}>
              <div style={{ display: 'flex', gap: '1em', alignItems: 'center' }}>
                <h2>Proposed Door Assessments</h2>
                <BwjLinkButton
                  icon={'Add'}
                  text='Add proposed door assessment'
                  onClick={onAddDoor}
                />
              </div>
              {formModel.installationAssessments.validity === BwjValidityState.Invalid &&
              <BwjText textSize={BwjTextSize.small} style={{ color: 'red', display: 'block' }}>{formModel.installationAssessments.message}</BwjText>}
            </Col>
          </Row>
          <Row bottomSpacing={Spacing.Large}>
            <Col small={12}>
              {formModel.installationAssessments.value.map(door =>
                <ProposedDoorListing
                  key={door.id}
                  door={door}
                  doorTypes={editorModel.current.doorTypeOptions}
                  onSelect={onEditDoor}
                  onRemove={onDoorRemovalRequested}
                />
              )}
            </Col>
          </Row>
        </BwjGrid>
      </BwjPageContent>
    </BwjViewManager>
    </>
  );
};